/**
 * @file notesDoc.ts
 * @brief Meeting Notes v2 (`notes_doc`) on the web: types, a tolerant zod
 *        parse, the flat id list the evidence panel reads, and the dev-only
 *        preview.
 *
 * @details
 * DES 09-10-2026, web port of the phone's notesDoc.ts (07-10-2026,
 * docs/meeting-notes-v2-plan.md). A Notes meeting's notes are topic sections
 * of labelled bullets, open questions and next steps, each with the same
 * evidence anchor Meeting Intel items carry. A click on any line opens the
 * meeting's "where it was said" panel under it (MeetingEvidenceInline).
 *
 * Field: the raw payload's `notes_doc` (snake_case column, camelCase inside),
 * as the phone reads it (boardroomMeetingsStorage notesDocFields). A server
 * without the column, or a doc this view cannot draw, parses as null, so the
 * meeting keeps the v1 summary.
 *
 * Ids: every line gets `notesv2_<n>`, numbered in reading order (bullets,
 * their children, open questions, then next steps). The view numbers through
 * `flattenNotesDoc` only, so a row and its anchor can never disagree.
 *
 * Preview (dev only, the phone's rules): the committed fixture is `{}`. A
 * local copy (skip-worktree, never committed) holds `{ meetingId, doc }`. Only
 * in a dev build, only when the meeting is opened from Notes, Meeting notes
 * (the `notesPreview=1` query), and only for the fixture's meeting, does that
 * meeting show as a Notes meeting with that doc. From Boardrooms the same
 * meeting keeps its full Intel. Nothing is written anywhere.
 *
 * @references
 * - FRONTEND/src/screens/MeetingIntelligence/MeetingDetails/notesDoc.ts
 * - BACKEND (feat/notes-v2) src/features/boardroom/notesTier.mjs (normalizeNotesV2Result)
 * - components/MeetingNotesDocView.tsx
 *
 * @author DES
 * @date 09-10-2026
 */

import { z } from "zod";
import previewFixtureRaw from "./notesV2Preview.fixture.json";

export type NotesDocLabel = "confirmed" | "suggestion" | "open" | "unclear";

export type NotesDocAnchor = {
  startSecs: number;
  endSecs: number;
  segmentId?: string | null;
  quote?: string | null;
  confidence?: string;
};

export type NotesDocBullet = {
  text: string;
  label: NotesDocLabel | null;
  speaker: string | null;
  /** The owner's own typed point leads this bullet. */
  typed?: boolean;
  anchor: NotesDocAnchor | null;
  children?: NotesDocBullet[];
};

export type NotesDocQuestion = { text: string; anchor: NotesDocAnchor | null };

export type NotesDocSection = {
  heading: string;
  bullets: NotesDocBullet[];
  openQuestions: NotesDocQuestion[];
};

export type NotesDocStep = {
  task: string;
  owner: string | null;
  context: string | null;
  anchor: NotesDocAnchor | null;
};

export type NotesDoc = {
  version: number;
  overview: string;
  sections: NotesDocSection[];
  nextSteps: NotesDocStep[];
  alsoInYourNotes: string[];
};

export const NOTES_DOC_ID_PREFIX = "notesv2_";

// ----------------------------------------------------------------------
// Tolerant parse: a bad field drops to its empty value, never the meeting.
// ----------------------------------------------------------------------
const nullableString = z.string().nullable().optional().catch(null).transform((v) => v ?? null);

const AnchorSchema: z.ZodType<NotesDocAnchor | null, z.ZodTypeDef, unknown> = z
  .object({
    startSecs: z.number(),
    endSecs: z.number(),
    segmentId: z.string().nullable().optional().catch(null),
    quote: z.string().nullable().optional().catch(null),
    confidence: z.string().optional().catch(undefined),
  })
  .passthrough()
  .nullable()
  .catch(null);

const BulletSchema: z.ZodType<NotesDocBullet, z.ZodTypeDef, unknown> = z.lazy(() =>
  z.object({
    text: z.string().catch(""),
    label: z
      .enum(["confirmed", "suggestion", "open", "unclear"])
      .nullable()
      .optional()
      .catch(null)
      .transform((v) => v ?? null),
    speaker: nullableString,
    typed: z.boolean().optional().catch(undefined),
    anchor: AnchorSchema.optional().transform((v) => v ?? null),
    children: z.array(BulletSchema).optional().catch([]),
  }),
);

const QuestionSchema = z.object({
  text: z.string().catch(""),
  anchor: AnchorSchema.optional().transform((v) => v ?? null),
});

const StepSchema = z.object({
  task: z.string().catch(""),
  owner: nullableString,
  context: nullableString,
  anchor: AnchorSchema.optional().transform((v) => v ?? null),
});

/** `notes_doc` on the meeting payload; anything undrawable reads as null. */
export const NotesDocSchema: z.ZodType<NotesDoc | null, z.ZodTypeDef, unknown> = z
  .object({
    version: z.coerce.number().catch(2),
    overview: z.string().catch(""),
    sections: z.array(
      z.object({
        heading: z.string().catch(""),
        bullets: z.array(BulletSchema),
        openQuestions: z.array(QuestionSchema).optional().catch([]).transform((v) => v ?? []),
      }),
    ),
    nextSteps: z.array(StepSchema).optional().catch([]).transform((v) => v ?? []),
    alsoInYourNotes: z
      .array(z.unknown())
      .optional()
      .catch([])
      .transform((v) => (v ?? []).filter((p): p is string => typeof p === "string")),
  })
  .nullable()
  .catch(null);

/** Accept only a doc the view can draw; anything else is treated as absent. */
export function readNotesDoc(raw: unknown): NotesDoc | null {
  if (raw == null) return null;
  const parsed = NotesDocSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

export type NotesDocLine = { id: string; text: string; anchor: NotesDocAnchor | null };

/** Every line in reading order, with its evidence id (the phone's order). */
export function flattenNotesDoc(doc: NotesDoc | null | undefined): NotesDocLine[] {
  const out: NotesDocLine[] = [];
  if (!doc) return out;
  const push = (text: string, anchor: NotesDocAnchor | null) =>
    out.push({ id: `${NOTES_DOC_ID_PREFIX}${out.length}`, text, anchor });
  for (const s of doc.sections) {
    for (const b of s.bullets) {
      push(b.text, b.anchor);
      for (const c of b.children ?? []) push(c.text, c.anchor);
    }
    for (const q of s.openQuestions) push(q.text, q.anchor);
  }
  for (const st of doc.nextSteps) push(st.task, st.anchor);
  return out;
}

// ----------------------------------------------------------------------
// Dev preview (DES 09-10-2026, the phone's 07-10-2026 rules). The committed
// fixture is `{}`. To try a meeting, write a dry run into it locally
// (BACKEND scripts/eval-notes-v2.mjs output: { "meetingId": "<id>", "doc":
// <result.doc> }) and keep it out of git with
// `git update-index --skip-worktree`. That is a real meeting's content.
// `import.meta.env.DEV` is false in a production build, so the fixture is
// never read there and the bundler drops it.
// ----------------------------------------------------------------------
const NOTES_V2_PREVIEW_ON = import.meta.env.DEV;

/** The query a Notes-page row adds so the detail page may show the preview. */
export const NOTES_PREVIEW_QUERY = "notesPreview";

type PreviewFixture = { meetingId: string; doc: NotesDoc };

function readPreviewFixture(): PreviewFixture | null {
  if (!NOTES_V2_PREVIEW_ON) return null;
  const raw: unknown = previewFixtureRaw;
  if (!raw || typeof raw !== "object") return null;
  const r = raw as { meetingId?: unknown; doc?: unknown };
  if (typeof r.meetingId !== "string" || !r.meetingId) return null;
  const doc = readNotesDoc(r.doc);
  return doc ? { meetingId: r.meetingId, doc } : null;
}

const previewFixture = readPreviewFixture();

/** The meeting the preview is on (listed in Meeting notes), or null. */
export const NOTES_V2_PREVIEW_MEETING_ID: string | null = previewFixture?.meetingId ?? null;

/** The preview doc, for the Notes list row's title fallback. Dev only. */
export function previewNotesDoc(): NotesDoc | null {
  return previewFixture?.doc ?? null;
}

/**
 * The preview doc for this meeting when it was opened from Meeting notes, or
 * null. Dev builds only; never for a meeting that has its own doc.
 */
export function previewNotesDocFor(
  meetingId: string | null | undefined,
  openedFromNotes: boolean,
): NotesDoc | null {
  if (!NOTES_V2_PREVIEW_ON || !previewFixture || !openedFromNotes) return null;
  return previewFixture.meetingId === meetingId ? previewFixture.doc : null;
}

/** The detail URL's preview flag (`?notesPreview=1`). */
export function hasNotesPreviewFlag(search: string): boolean {
  if (!NOTES_V2_PREVIEW_ON) return false;
  return new URLSearchParams(search).get(NOTES_PREVIEW_QUERY) === "1";
}
